import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/pages/home/components/Footer";
import { useTheme } from "@/context/ThemeContext";
import { track } from "@/utils/track";
import { DOWNLOAD_URLS } from "@/config/download";

interface Channel {
  id: string;
  icon: string;
  labelKey: string;
  href: string;
  external: boolean;
  hintKey?: string;
}

const CHANNELS: Channel[] = [
  {
    id: "google-play",
    icon: "ri-google-play-fill",
    labelKey: "download.google_play",
    href: DOWNLOAD_URLS.googlePlay,
    external: true,
  },
  {
    id: "apk",
    icon: "ri-android-fill",
    labelKey: "download.apk",
    href: DOWNLOAD_URLS.apk,
    external: false,
    hintKey: "download.apk_hint",
  },
];

export default function DownloadPage() {
  const { t } = useTranslation("home");
  const { effectiveTheme } = useTheme();
  const isLight = effectiveTheme === "light";
  const [toastVisible, setToastVisible] = useState(false);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  const showComingSoonToast = () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToastVisible(true);
    toastTimerRef.current = setTimeout(() => setToastVisible(false), 2500);
  };

  const handleChannelClick = (channel: Channel) => {
    track("download_click", { toolId: `download-page-${channel.id}` });
    if (channel.id === "app-store" && !channel.href) {
      showComingSoonToast();
    }
  };

  const cardClass = `flex flex-col items-center gap-4 rounded-2xl border px-6 py-8 text-center transition-all duration-200 cursor-pointer ${
    isLight
      ? "bg-white border-black/10 hover:border-black/25 hover:shadow-lg"
      : "bg-cosmic-900/60 border-white/10 hover:border-gold-500/40 hover:bg-cosmic-900"
  }`;

  return (
    <div
      className={`relative min-h-screen flex flex-col overflow-x-hidden ${
        isLight ? "bg-[#f5f5f7] text-[#1c1c1e]" : "bg-cosmic-950 text-white"
      }`}
    >
      <Navbar />
      <main className="relative z-10 flex-1 flex items-center justify-center px-6 sm:px-10 lg:px-16 pt-28 pb-20">
        <div className="w-full max-w-4xl">
          <div className="flex flex-col items-center text-center mb-10">
            <img
              src="/images/app-icon-512.webp"
              alt={t("download.title")}
              className="w-20 h-20 md:w-24 md:h-24 rounded-3xl object-contain mb-6"
            />
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">
              {t("download.title")}
            </h1>
            <p className={`mt-3 text-sm sm:text-base ${isLight ? "text-[#6e6e73]" : "text-silver-400"}`}>
              {t("download.subtitle")}
            </p>
          </div>

          <div
            className={`grid grid-cols-1 gap-4 ${
              CHANNELS.length <= 1
                ? "sm:grid-cols-1"
                : CHANNELS.length === 2
                  ? "sm:grid-cols-2"
                  : "sm:grid-cols-3"
            }`}
          >
            {CHANNELS.map((channel) => {
              const isComingSoon = channel.id === "app-store" && !channel.href;
              const inner = (
                <>
                  <div
                    className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                      isLight ? "bg-gold-500/10" : "bg-gold-500/15"
                    }`}
                  >
                    <i className={`${channel.icon} text-3xl ${isLight ? "text-gold-500" : "text-gold-400"}`} />
                  </div>
                  <div>
                    <p className="text-base font-bold">{t(channel.labelKey)}</p>
                    {channel.hintKey && (
                      <p className={`text-xs mt-1 ${isLight ? "text-[#6e6e73]" : "text-silver-500"}`}>
                        {t(channel.hintKey)}
                      </p>
                    )}
                  </div>
                </>
              );

              if (isComingSoon) {
                return (
                  <button key={channel.id} onClick={() => handleChannelClick(channel)} className={cardClass}>
                    {inner}
                  </button>
                );
              }

              return (
                <a
                  key={channel.id}
                  href={channel.href}
                  target={channel.external ? "_blank" : undefined}
                  rel={channel.external ? "noopener noreferrer" : undefined}
                  onClick={() => handleChannelClick(channel)}
                  className={cardClass}
                >
                  {inner}
                </a>
              );
            })}
          </div>
        </div>
      </main>
      <Footer />

      {toastVisible && (
        <div className="fixed left-1/2 -translate-x-1/2 bottom-16 z-[200] px-4 py-2.5 rounded-full text-xs sm:text-sm font-medium bg-black/80 text-white shadow-lg">
          {t("download.coming_soon")}
        </div>
      )}
    </div>
  );
}
